import {Inject, Injectable} from "@angular/core";
import {map, Observable, of, ReplaySubject, share, Subject} from "rxjs";
import {ConfigurationServiceConstants} from "./configuration-service-constants";
import {HttpClient, HttpResponse} from "@angular/common/http";
import {StringUtils} from "../utils/string-utils";

/**
 * This is a cut down version of ConfigurationService, meant to fetch bundled 'static' and local-storage configurations only.
 * It is safe to use without a backend connection to obtain configurations.
 * NOTE: values saved using LocalConfigurationService will not update the cache of ConfigurationService (and vice-versa).
 * For this reason, use of the service should be restricted to special cases where a login has not yet occurred and ConfigurationService has not yet been used.
 */
@Injectable()
export class LocalConfigurationService {
    private localConfigurationsObservable: Observable<any>;
    private _configurationChanges: Subject<string> = new Subject<string>();
    private pathName: string;

    constructor(private localHttpClient: HttpClient,
                @Inject(ConfigurationServiceConstants.APPLICATION_KEY) private _application: string,
                @Inject(ConfigurationServiceConstants.LOCAL_CONFIG_FILE_KEY) private localConfigFile: string,
                @Inject(ConfigurationServiceConstants.CONTEXT_NAMES_KEY) private _contextNames: string[]) {
        this.pathName = window.location.pathname;
    }

    /**
     * Returns an observable sequence of changed config specs.
     * @returns {Observable}
     */
    get configurationChanges(): Observable<string> {
        return this._configurationChanges;
    }

    setLocalConfigFile(localConfigFile: string) {
        this.localConfigFile = localConfigFile;
        this.localConfigurationsObservable = undefined;
    }

    public getString(configSpec: string, dflt?: string): Promise<string> {
        let match: string[] = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match || match[1] !== 'local') {
            return Promise.resolve(null);
        }
        let configName: string = match[2];
        return new Promise<string>((resolve, reject) => {
            this.loadLocalConfigurations().subscribe(
                (configurations: any) => {
                    if (!configurations.hasOwnProperty(configName)) {
                        resolve(dflt);
                    }

                    resolve(configurations[configName]);
                }
            );
        })
    }

    public getBoolean(configSpec: string, dflt: boolean = false): Promise<boolean> {
        return this.getString(configSpec).then((value: string) => value === undefined ? dflt : value.toLowerCase() === 'true');
    }

    public getNumber(configSpec: string, dflt?: number): Promise<number> {
        return this.getString(configSpec).then((value: string) => {
            if (value === undefined) {
                return dflt;
            }
            let i: number = parseInt(value);
            return isNaN(i) ? undefined : i;
        });
    }

    /**
     * Saves a local configuration.
     * The configuration is persisted in local browser-based storage against the default context.
     * @param configSpec
     * @param value
     * @param storeInSession Indicates that the value is stored only for the session
     * @returns A boolean (true on success) indicating that the configuration has been saved
     */
    public saveString(configSpec: string, value: string, storeInSession?: boolean): boolean {
        let match: string[] = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match) {
            return false;
        }
        let category: string = match[1];
        let configName: string = match[2];
        if (category !== 'local') {
            return false;
        }

        const storage: Storage = storeInSession ? sessionStorage : localStorage;
        let localConfigurations: any = JSON.parse(storage.getItem(this.localStorageKey)) || {};
        ConfigurationServiceConstants.setConfiguration(localConfigurations, category, configName, "default", {}, value);
        storage.setItem(this.localStorageKey, JSON.stringify(localConfigurations));

        // Invalidate cache - reload is inexpensive and saves should be rare
        this.localConfigurationsObservable = null;

        this._configurationChanges.next(configSpec);
        return true;
    }

    public saveBoolean(configSpec: string, value: boolean, storeInSession?: boolean): boolean {
        return this.saveString(configSpec, value.toString(), storeInSession);
    }

    public saveNumber(configSpec: string, value: number, storeInSession?: boolean): boolean {
        return this.saveString(configSpec, value.toString(), storeInSession);
    }

    private get localStorageKey(): string {
        return this.pathName + "/" + ConfigurationServiceConstants.LOCAL_STORAGE_KEY;
    }

    /**
     * Returns an observable for the configuration data from the local configuration file.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    private loadLocalConfigurations(): Observable<any> {
        // Only allow a single concurrent request
        if (this.localConfigurationsObservable) {
            return this.localConfigurationsObservable;
        }
        if (!this.localConfigFile) {
            return of({});
        }

        return this.localConfigurationsObservable = this.localHttpClient.get(this.localConfigFile, {
            observe: "response",
            responseType: "text"
        }).pipe(
            map((response: HttpResponse<string>) => {
                let configurations: any = JSON.parse(StringUtils.stripJsonComments(response.body));

                // local storage (saved) configurations
                this.loadFromStorage(configurations, localStorage);

                // session storage (saved) configurations
                this.loadFromStorage(configurations, sessionStorage);

                return configurations;
            }),
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );
    }

    private loadFromStorage(configurations: any, storage: Storage): void {
        let localConfigurations: any = JSON.parse(storage.getItem(this.localStorageKey)) || {};
        if (localConfigurations.hasOwnProperty("local")) {
            let configurationMap: {
                [configName: string]: { [contextSpecKey: string]: string }
            } = localConfigurations["local"];
            for (let configName in configurationMap) {
                if (configurationMap.hasOwnProperty(configName)) {
                    let contextSpecMap: { [contextSpecKey: string]: string } = configurationMap[configName];
                    if (contextSpecMap.hasOwnProperty("default")) {
                        configurations[configName] = contextSpecMap["default"];
                    }
                }
            }
        }
    }

    public get application() {
        return this._application;
    }

    public get contextNames() {
        return this._contextNames;
    }
}
